import React, { useState, useCallback, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { accountAPI, memberAPI, reservationsAPI, coursesAPI, ssoAPI, communityAPI, signedIn, signOut, getMember } from "../../services/portalApi";
import ReservationView, { STATUS } from "../booking/ReservationView";
import { money, longDate, dateRange } from "../../services/api";
import Footer from "../home/footer";
import "../booking/booking.css";
import "./account.css";
import "./courses.css";
import { Progress } from "./CoursePage";
import Picture from "../utils/Picture";

/* ══════════════════════════════════════════════════════════
   Signing in — the same six-digit code the booking flow uses,
   so someone who has booked before doesn't make a second
   account to look at the first one.
══════════════════════════════════════════════════════════ */
export function SignIn({ onDone, eyebrow = "Your account", intro, allowRegister = true }) {
  const [stage, setStage] = useState("email");   // email | code
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [code, setCode] = useState("");
  const [needName, setNeedName] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const sendCode = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await accountAPI.requestCode(email.trim());
      setStage("code");
    } catch (err) {
      /* No account on that address yet — ask for a name and make one. */
      if (err.status === 404 || /not found|no account/i.test(err.message)) {
        if (allowRegister) { setNeedName(true); setError(""); }
        else setError("There's no account for that email. Use the address we set up for you, or ask us.");
      } else {
        setError(err.message);
      }
    } finally { setBusy(false); }
  };

  const createAndSend = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await accountAPI.register({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
      });
      setNeedName(false);
      setStage("code");
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await accountAPI.verifyCode(email.trim(), code.trim());
      onDone();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="mk-wrap">
      <p className="mk-eyebrow">{eyebrow}</p>
      <h1 className="mk-title">
        {stage === "code" ? "Check your email." : "Sign in with your email."}
      </h1>
      <p className="mk-muted mk-sub">
        {stage === "code"
          ? `We've sent a six-digit code to ${email}. It's good for ten minutes.`
          : intro || "No password to remember — we'll email you a code. Use the same address you booked with and everything will be here."}
      </p>

      <div className="mk-card">
        {stage === "email" && !needName && (
          <form className="mk-form" onSubmit={sendCode}>
            <label className="mk-field">
              <span>Email</span>
              <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setError(""); }}
                required autoComplete="email" autoFocus />
            </label>
            {error && <p className="mk-error">{error}</p>}
            <div className="mk-actions">
              <button className="mk-btn mk-btn--primary" disabled={busy}>
                {busy ? "SENDING…" : "EMAIL ME A CODE"}
              </button>
            </div>
          </form>
        )}

        {needName && (
          <form className="mk-form" onSubmit={createAndSend}>
            <p className="mk-muted mb-0">
              We don't have an account for {email} yet. Tell us your name and we'll make one.
            </p>
            <div className="mk-row">
              <label className="mk-field">
                <span>First name</span>
                <input value={firstName} onChange={(e) => { setFirstName(e.target.value); setError(""); }}
                  required autoComplete="given-name" autoFocus />
              </label>
              <label className="mk-field">
                <span>Last name</span>
                <input value={lastName} onChange={(e) => { setLastName(e.target.value); setError(""); }}
                  required autoComplete="family-name" />
              </label>
            </div>
            {error && <p className="mk-error">{error}</p>}
            <div className="mk-actions">
              <button className="mk-btn mk-btn--primary" disabled={busy}>
                {busy ? "SENDING…" : "CREATE AND SEND CODE"}
              </button>
              <button type="button" className="mk-btn mk-btn--ghost"
                onClick={() => { setNeedName(false); setError(""); }}>USE ANOTHER EMAIL</button>
            </div>
          </form>
        )}

        {stage === "code" && (
          <form className="mk-form" onSubmit={verify}>
            <label className="mk-field">
              <span>Your code</span>
              <input className="mk-code" inputMode="numeric" maxLength={6} value={code}
                onChange={(e) => { setCode(e.target.value.replace(/\D/g, "")); setError(""); }}
                required autoFocus />
            </label>
            {error && <p className="mk-error">{error}</p>}
            <div className="mk-actions">
              <button className="mk-btn mk-btn--primary" disabled={busy || code.length < 6}>
                {busy ? "CHECKING…" : "SIGN IN"}
              </button>
              <button type="button" className="mk-btn mk-btn--ghost"
                onClick={() => { setStage("email"); setCode(""); setError(""); }}>
                USE ANOTHER EMAIL
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Signed in
══════════════════════════════════════════════════════════ */
const STATUS_WORD = {
  confirmed: "Confirmed", pending: "Pending", cancelled: "Cancelled",
  waitlist: "Waiting list", new: "Received", paid: "Paid",
  packed: "Being packed", shipped: "Shipped", collected: "Collected",
};

function Section({ title, empty, children, count }) {
  return (
    <section className="acc-section">
      <h2 className="acc-section-title">
        {title}
        {count > 0 && <span className="acc-pill">{count}</span>}
      </h2>
      {count === 0 ? <p className="acc-empty">{empty}</p> : children}
    </section>
  );
}

export default function AccountPage() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(signedIn());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [params, setParams] = useSearchParams();
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      // Reservations come from their own endpoint; a site without them (or
      // before the v2 migration) simply shows none.
      const [account, res, courses] = await Promise.all([
        memberAPI.account(),
        reservationsAPI.mine().catch(() => ({ reservations: [], diets: [] })),
        coursesAPI.list().catch(() => []),
      ]);
      setData({ ...account, reservations: res.reservations || [], diets: res.diets || [], courses: Array.isArray(courses) ? courses : [] });
    } catch (err) {
      if (err.status === 401) { setAuthed(false); return; }
      setError(err.message);
    } finally { setLoading(false); }
  }, []);

  /* Back from Stripe: ?paid=REF&session_id=… or ?unpaid=REF. The payment
     is recorded from here as well as by Stripe's own notice, whichever
     comes first; it's only ever counted once. */
  useEffect(() => {
    if (!authed) return;
    const paid = params.get("paid"), sid = params.get("session_id"), unpaid = params.get("unpaid");
    if (!paid && !unpaid) { load(); return; }
    setParams({}, { replace: true });
    if (unpaid) {
      setNotice({ tone: "warn", text: `Your card payment for ${unpaid} wasn't completed. Nothing was charged; you can try again below.` });
      load();
      return;
    }
    reservationsAPI.cardReturn(paid, sid)
      .then((res) => setNotice(res.paid
        ? { tone: "ok", text: res.status === "confirmed" ? `Payment received. ${paid} is confirmed. We've emailed you the details.` : `Payment received for ${paid}. Thank you.` }
        : { tone: "warn", text: "Your payment is still processing. We'll email you as soon as it clears." }))
      .catch((err) => setNotice({ tone: "warn", text: err.message }))
      .finally(load);
  }, [authed]); // eslint-disable-line react-hooks/exhaustive-deps

  const leave = () => { signOut(); setAuthed(false); setData(null); };

  if (!authed) {
    return (
      <>
        <div className="mk-page grain-bg-light">
          <SignIn onDone={() => setAuthed(true)} />
        </div>
        <Footer />
      </>
    );
  }

  const member = data?.member || getMember() || {};
  const bookings = data?.bookings || [];
  const workshops = data?.workshops || [];
  const orders = data?.orders || [];
  const reservations = data?.reservations || [];
  const replace = (r) => setData((cur) => ({ ...cur, reservations: cur.reservations.map((x) => (x.ref === r.ref ? r : x)) }));

  return (
    <>
      <div className="mk-page grain-bg-light">
        <div className="mk-wrap mk-wrap--wide">
          <div className="acc-head">
            <div>
              <p className="mk-eyebrow">Your account</p>
              <h1 className="mk-title">
                {member.firstName ? `Hello, ${member.firstName}.` : "Hello."}
              </h1>
              <p className="mk-muted mb-0">{member.email}</p>
            </div>
            <button className="mk-btn mk-btn--ghost" onClick={leave}>SIGN OUT</button>
          </div>

          {notice && <p className={`acc-notice acc-notice--${notice.tone}`} role="status">{notice.text}</p>}
          {loading && <p className="mk-muted">Loading…</p>}
          {error && <p className="mk-error">{error}</p>}

          {!loading && !error && data && (
            <>
              <Section
                title="Your retreats"
                count={reservations.length}
                empty="No retreat reservations yet."
              >
                <div className="acc-list">
                  {reservations.map((r, i) => <ReservationCard key={r.ref} r={r} first={i === 0} diets={data.diets} onChange={replace} />)}
                </div>
              </Section>

              {data.courses.length > 0 && (
                <Section title="Preparation" count={data.courses.length} empty="">
                  <p className="mk-muted acc-intro">Short courses from our team to help you arrive ready, and to carry it home afterwards.</p>
                  <div className="crs-cards">
                    {data.courses.map((c) => <CourseCard key={c.id} c={c} />)}
                  </div>
                </Section>
              )}

              {bookings.length > 0 && <Section
                title="Earlier bookings"
                count={bookings.length}
                empty=""
              >
                <div className="acc-list">
                  {bookings.map((b) => (
                    <article className="acc-card" key={b.id}>
                      <div className="acc-card-main">
                        <p className="acc-when">{dateRange(b.startDate, b.endDate)}</p>
                        <h3 className="acc-name">{b.pathway || b.retreat}</h3>
                        <p className="acc-meta">
                          {[b.location, b.room, b.activity, b.diet].filter(Boolean).join(" · ")}
                          {b.nights ? ` · ${b.nights} nights` : ""}
                        </p>
                      </div>
                      <div className="acc-card-side">
                        <span className={`acc-status is-${b.status}`}>{STATUS_WORD[b.status] || b.status}</span>
                        <span className="acc-total">{money(b.total)}</span>
                      </div>
                    </article>
                  ))}
                </div>
              </Section>}

              <Section
                title="Workshops"
                count={workshops.length}
                empty="No workshop sign-ups yet."
              >
                <div className="acc-list">
                  {workshops.map((w) => (
                    <article className="acc-card" key={w.id}>
                      <div className="acc-card-main">
                        <p className="acc-when">{longDate(w.startsAt)}</p>
                        <h3 className="acc-name">{w.title}</h3>
                        <p className="acc-meta">
                          {[w.location, w.places > 1 ? `${w.places} places` : null].filter(Boolean).join(" · ")}
                        </p>
                      </div>
                      <div className="acc-card-side">
                        <span className={`acc-status is-${w.status}`}>{STATUS_WORD[w.status] || w.status}</span>
                        <span className="acc-total">{w.price > 0 ? money(w.price) : "Free"}</span>
                      </div>
                    </article>
                  ))}
                </div>
              </Section>

              <Section
                title="Orders"
                count={orders.length}
                empty="No orders yet."
              >
                <div className="acc-list">
                  {orders.map((o) => (
                    <article className="acc-card acc-card--order" key={o.id}>
                      <div className="acc-card-main">
                        <p className="acc-when">{longDate(o.placedAt)} · {o.reference}</p>
                        <ul className="acc-items">
                          {o.items.map((it, i) => (
                            <li key={i}>
                              {it.quantity}× {it.name}
                              {(it.colour || it.size) && (
                                <span className="acc-items-opt"> — {[it.colour, it.size].filter(Boolean).join(" · ")}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                        <p className="acc-meta">
                          {o.fulfilment === "collect" ? "Collecting" : "Shipping"}
                          {o.shipping > 0 ? ` · ${money(o.shipping)} shipping` : ""}
                        </p>
                      </div>
                      <div className="acc-card-side">
                        <span className={`acc-status is-${o.status}`}>{STATUS_WORD[o.status] || o.status}</span>
                        <span className="acc-total">{money(o.total)}</span>
                      </div>
                    </article>
                  ))}
                </div>
              </Section>

              <Community setNotice={setNotice} />
              <SharedSignIn onSignedOut={leave} setNotice={setNotice} />

              {reservations.length === 0 && bookings.length === 0 && workshops.length === 0 && orders.length === 0 && (
                <div className="mk-actions">
                  <button className="mk-btn mk-btn--primary" onClick={() => navigate("/retreats")}>SEE THE RETREATS</button>
                  <button className="mk-btn mk-btn--ghost" onClick={() => navigate("/shop")}>VISIT THE SHOP</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Footer />
    </>
  );
}

/**
 * The community forum, shared with Forge Retreat Colombia. Shown only
 * while it's on. Signing in to it goes through Forge, so the first visit
 * asks before making a Forge account (name and email only).
 */
function Community({ setNotice }) {
  const [forum, setForum] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { communityAPI.info().then(setForum).catch(() => {}); }, []);
  if (!forum?.enabled) return null;
  const go = async () => {
    setBusy(true);
    try { window.location.assign((await ssoAPI.handoff("forum")).url); }
    catch (e) { setNotice({ tone: "warn", text: e.message }); setBusy(false); }
  };
  return (
    <section className="acc-section">
      <h2 className="acc-section-title">Community</h2>
      <p className="mk-muted acc-intro">
        Talk with others before you arrive and stay in touch afterwards. Everyone on your retreat dates shares a private space.
      </p>
      <div className="mk-actions acc-sso">
        <button type="button" className="mk-btn mk-btn--primary" disabled={busy} onClick={go}>
          {busy ? "Opening…" : "Go to the community"}
        </button>
      </div>
    </section>
  );
}

/**
 * The same sign-in works at Forge Retreat Colombia (an account there,
 * nothing else). Signing out everywhere ends every session on both sites.
 */
function SharedSignIn({ onSignedOut, setNotice }) {
  const [busy, setBusy] = useState("");
  const go = async () => {
    setBusy("go");
    try { window.location.assign((await ssoAPI.handoff()).url); }
    catch (e) { setNotice({ tone: "warn", text: e.message }); setBusy(""); }
  };
  const outEverywhere = async () => {
    if (!window.confirm("Sign out on every device, here and at Forge Retreat Colombia?")) return;
    setBusy("out");
    try { await ssoAPI.logoutEverywhere(); onSignedOut(); }
    catch (e) { setNotice({ tone: "warn", text: e.message }); setBusy(""); }
  };
  return (
    <section className="acc-section">
      <h2 className="acc-section-title">Your sign-in</h2>
      <p className="mk-muted acc-intro">
        This sign-in also works at Forge Retreat Colombia, our sister organisation. The first time you go there, you can
        agree to an account with your name and email. Nothing else about you is shared.
      </p>
      <div className="mk-actions acc-sso">
        <button type="button" className="mk-btn mk-btn--ghost" disabled={!!busy} onClick={go}>
          {busy === "go" ? "Opening…" : "Continue to Forge Retreat Colombia"}
        </button>
        <button type="button" className="mk-btn mk-btn--ghost" disabled={!!busy} onClick={outEverywhere}>
          {busy === "out" ? "Signing out…" : "Sign out everywhere"}
        </button>
      </div>
    </section>
  );
}

/**
 * One reservation: a line to scan, opened to the full view. Before
 * arrival each guest can change their food choices here — the kitchen
 * sheet reads them.
 */
const opensOn = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric" });

/** A course in the account: open ones link to it, others say when they open. */
function CourseCard({ c }) {
  const body = (
    <>
      <div className="crs-card-img">{c.thumbnail && <Picture src={c.thumbnail} alt="" width={480} height={210} sizes="(min-width: 900px) 280px, 100vw" />}</div>
      <div className="crs-card-body">
        <h3 className="crs-card-title hFont">{c.title}</h3>
        {c.description && <p className="crs-card-desc">{c.description}</p>}
        {c.open
          ? <Progress p={c.progress} />
          : <p className="crs-when">{c.waitingFor === "clearance" ? "Opens once your medical screening has cleared" : `Opens ${opensOn(c.opensAt)}`}</p>}
      </div>
    </>
  );
  return c.open
    ? <Link className="crs-card" to={`/account/courses/${c.id}`}>{body}</Link>
    : <div className="crs-card is-locked">{body}</div>;
}

function ReservationCard({ r, first, diets, onChange }) {
  // The newest reservation opens if it's waiting on payment; the rest stay folded.
  const [open, setOpen] = useState(first && r.status === "pending_payment");
  const editable = ["pending_payment", "confirmed", "requested"].includes(r.status) && r.arrive_date > new Date().toISOString().slice(0, 10);
  return (
    <article className="acc-res">
      <button type="button" className="acc-res-line" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="acc-card-main">
          <span className="acc-when">{dateRange(r.arrive_date, r.depart_date)} · {r.ref}</span>
          <span className="acc-name">{r.retreat_name}</span>
        </span>
        <span className="acc-card-side">
          <span className={`acc-status is-${r.status}`}>{STATUS[r.status]?.label || r.status}</span>
          <span className="acc-total">{r.balance > 0 && r.status !== "cancelled" ? `${money(r.balance)} to pay` : money(r.total)}</span>
        </span>
      </button>
      {open && (
        <div className="acc-res-body">
          <ReservationView r={r} />
          {editable && diets.length > 0 && <FoodEditor r={r} diets={diets} onChange={onChange} />}
        </div>
      )}
    </article>
  );
}

function FoodEditor({ r, diets, onChange }) {
  const exclusive = new Set(diets.filter((d) => d.exclusive).map((d) => d.id));
  const [drafts, setDrafts] = useState(() => Object.fromEntries(r.guests.map((g) => [g.id, { diets: g.diets.map((d) => d.id), notes: g.dietary_notes || "" }])));
  const [saving, setSaving] = useState(null);
  const [msg, setMsg] = useState({});

  const toggle = (gid, id) => setDrafts((cur) => {
    const has = cur[gid].diets.includes(id);
    const next = has ? cur[gid].diets.filter((x) => x !== id) : exclusive.has(id) ? [id] : [...cur[gid].diets.filter((x) => !exclusive.has(x)), id];
    return { ...cur, [gid]: { ...cur[gid], diets: next } };
  });
  const save = async (g) => {
    setSaving(g.id); setMsg((m) => ({ ...m, [g.id]: "" }));
    try {
      const fresh = await reservationsAPI.updateGuest(r.ref, g.id, { diets: drafts[g.id].diets, dietary_notes: drafts[g.id].notes });
      onChange(fresh);
      setMsg((m) => ({ ...m, [g.id]: "Saved." }));
    } catch (err) { setMsg((m) => ({ ...m, [g.id]: err.message })); }
    finally { setSaving(null); }
  };

  return (
    <section className="acc-food" aria-label="Food choices">
      <h3 className="acc-food-title">Food</h3>
      <p className="mk-muted">Change these any time before you arrive. Select all that apply.</p>
      {r.guests.map((g) => (
        <div className="acc-food-guest" key={g.id}>
          <p className="bk-label">{g.first_name} {g.last_name}</p>
          <div className="bk-chips">
            {diets.map((d) => (
              <label key={d.id} className={`bk-chip ${drafts[g.id].diets.includes(d.id) ? "is-on" : ""}`}>
                <input type="checkbox" checked={drafts[g.id].diets.includes(d.id)} onChange={() => toggle(g.id, d.id)} />
                {d.name}
              </label>
            ))}
          </div>
          <label className="mk-field mt-3">
            <span>Allergies and dietary needs <em>optional</em></span>
            <textarea rows={2} maxLength={1000} value={drafts[g.id].notes}
              onChange={(e) => setDrafts((cur) => ({ ...cur, [g.id]: { ...cur[g.id], notes: e.target.value } }))} />
          </label>
          <div className="mk-actions">
            <button type="button" className="mk-btn mk-btn--ghost" disabled={saving === g.id} onClick={() => save(g)}>
              {saving === g.id ? "Saving…" : "Save food choices"}
            </button>
            {msg[g.id] && <span className="acc-saved" role="status">{msg[g.id]}</span>}
          </div>
        </div>
      ))}
    </section>
  );
}
